// components/ModalDialogFinancing.js
import React, { useState, useEffect } from 'react';
import './ModalDialogFinancing.css';
import './../spinner.css';
import './../pdf-test.css'; // scoped overrides for the parallel Test PDF pipeline
//import  './pdf.css';
import { toPdf_pages, toPdfBackend } from './toPdf';
import EmailDialog from '../records/EmailDialog';
import { USE_PDF_SERVER } from '../constants';

const ModalDialogFinancing = ({
  show,
  onClose,
  title = '',
  children,
  quoteId = null,
  // testMode=true: this instance was opened from the parallel "Test" button.
  // Its PDF action uses the new server-side Playwright pipeline + the
  // pdf-test.css override sheet, so we can iterate on PDF quality without
  // touching the production Packages/Promo-Financing flow.
  testMode = false,
  //customerEmail = null,
}) => {
  if (!show) return null;

  const [isLoading, setIsLoading] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isViewDetailsVisible, setIsViewDetailsVisible] = useState(true);
  const [recipient, setRecipient] = useState(""); // ← default empty string

  // Detect read-only and customerEmail from child (HandlePromo)
  const childProps = React.isValidElement(children) ? children.props : {};
  
  const customerEmail = children?.type?.customerEmail || null;
  //const customerEmail = React.isValidElement(children) ? children.props.customerEmail : null;
  //const customerEmail = childProps.customerEmail || "aaa@bbb.com";
  const isReadOnly = childProps.isReadOnly === true;

/*
  useEffect(() => {
    if (show) {
      setShowEmailDialog(false); // Reset every time modal opens
    }
  }, [show]);
  */
  // PDF Download
  const handlePdfDownload = async () => {
    setIsLoading(true);
  
    // ←←← NEW: temporary style for nice strikethrough lines in PDF only
    let pdfFixStyle = null;
  
    try {
      await new Promise(r => setTimeout(r, 500));
  
      // DETECT ACTIVE TAB FIRST — BEFORE ANYTHING ELSE
      const activeCapture = document.querySelector('.active-pdf-capture .pdf-capture');
      const directCapture = document.querySelector('.pdf-capture');
  
      let container;
      if (activeCapture) {
        container = '.active-pdf-capture .pdf-capture';
        console.log('%c[PDF] Using active tab capture', 'color: green');
      } else if (directCapture) {
        container = '.pdf-capture';
        console.log('%c[PDF] Using direct capture (HandlePromo)', 'color: blue');
      } else {
        throw new Error('No content to capture');
      }
  
      // FORCE LAYOUT — this is critical
      const targetEl = document.querySelector(container);
      if (targetEl) targetEl.offsetHeight;
  
      // ←←← NEW: inject PDF-only strikethrough replacement
      pdfFixStyle = document.createElement('style');
      pdfFixStyle.textContent = `
        ${container} s,
        ${container} del,
        ${container} [style*="text-decoration: line-through"],
        ${container} .line-through {
          text-decoration: none !important;
          background-image: linear-gradient(transparent 45%, currentColor 45%, currentColor 55%, transparent 55%) !important;
          background-repeat: repeat-x !important;
          background-size: 1px 1em !important;
          background-position: 0 0.62em !important;
        }
      `;
      document.head.appendChild(pdfFixStyle);
  
      // HIDE BUTTONS ONLY NOW — AFTER active tab is locked in
      document.querySelectorAll('.specs-master-control').forEach(el => {
        el.dataset.wasVisible = el.style.display !== 'none';
        el.style.display = 'none';
      });
  
      // PRELOAD LOGO ONCE — kills the 30 failed requests
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.src = '/assets/company_logo.png';
        img.onload = resolve;
        img.onerror = reject;
      });
  
      // Pipeline selection:
      //   * Always use the server pipeline when opened via the Test button
      //     (testMode=true) so we can keep iterating on pdf-test.css.
      //   * Otherwise honor the USE_PDF_SERVER flag from constants.js -
      //     the same flag the Email pipeline reads. One switch flips the
      //     whole app between vector (server) and raster (html2canvas).
      //
      // NOTE: we deliberately do NOT add the 'pdf-test-mode' class to the
      // live document.body. We only pass it as bodyClass on the standalone
      // HTML the backend renders. If we set it on the live DOM, the
      // .pdf-test-mode CSS overrides would briefly apply to the visible
      // dialog during the backend round-trip and make it look broken.
      if (testMode || USE_PDF_SERVER) {
        await toPdfBackend({
          container,
          filename: `quote-${quoteId ?? 'document'}${testMode ? '-test' : ''}`,
          format: 'A4',
          // margin: '0' tells Playwright not to add page margins so the
          // @page { margin: ... } rule from pdf-test.css fully controls them.
          margin: '0',
          bodyClass: 'pdf-test-mode',
        });
      } else {
        // Legacy raster pipeline (kept as fallback when USE_PDF_SERVER=false).
        await toPdf_pages({
          container,
          filename: `quote-${quoteId ?? 'document'}`,
          scale: 2,
          imageType: 'JPEG',
          imageQuality: 0.85
        });
      }
  
    } catch (err) {
      console.error('PDF failed:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      // ←←← NEW: clean up the temporary style (runs even if PDF fails)
      if (pdfFixStyle && pdfFixStyle.parentNode) {
        pdfFixStyle.parentNode.removeChild(pdfFixStyle);
      }
  
      // Restore buttons
      document.querySelectorAll('.specs-master-control').forEach(el => {
        if (el.dataset.wasVisible !== undefined) {
          el.style.display = '';
          delete el.dataset.wasVisible;
        }
      });
      setIsLoading(false);
    }
  };
  
  // Email
  const handleSendEmail = () => {
    setShowEmailDialog(true);
    setIsViewDetailsVisible(false);
  };

 
 /* // Clone children and pass customerEmail down
  const enhancedChildren = React.isValidElement(children)
    ? React.cloneElement(children, {
        customerEmail: customerInfo?.email || null,  // ← PASS FROM HandlePromo
      })
  : children;*/

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content-financing"
        onClick={(e) => e.stopPropagation()}
      >
        {title && <h2 style={{ marginTop: 0 }}>{title}</h2>}

        {/* Read-only banner */}
        {isReadOnly && (
          <div
            style={{
              background: '#fff3cd',
              border: '1px solid #ffe58f',
              color: '#d46b08',
              padding: '8px 12px',
              marginBottom: '12px',
              borderRadius: '4px',
              fontWeight: 'bold',
              fontFamily: 'monospace',
            }}
          >
            This quote is <strong>SUBMITTED</strong> – read-only mode.
          </div>
        )}

        <div className="modal-body" style={{ padding: 0 }}>
          {children}  {/* HandlePromo adds .pdf-capture per item */}
        </div>

        {/* Toolbar */}
        <div className="toolbar">
          <button className="toolbar-button" onClick={onClose}>
            Close
          </button>

          <button
            className={`toolbar-button ${isLoading ? 'loading' : ''}`}
            onClick={handlePdfDownload}
            disabled={isLoading || isReadOnly}
          >
            {isLoading ? <span className="spinner"></span> : 'PDF'}
          </button>

          <button
            className="toolbar-button"
            onClick={handleSendEmail}
          >
            Email
          </button>
        </div>
      </div>

      {/* Email Dialog */}
      {showEmailDialog && (
        <div onClick={(e) => e.stopPropagation()}>
          <EmailDialog
            simplePdf={false}
            isOpen={showEmailDialog}
            onClose={() => setShowEmailDialog(false)}
            isSending={isSending}
            setIsSending={setIsSending}
            //container=".active-pdf-capture .pdf-capture"
            container={document.querySelector('.active-pdf-capture .pdf-capture') 
              ? '.active-pdf-capture .pdf-capture' 
              : '.pdf-capture'
            }
            filename="document"
            /* subject/message intentionally not set so EmailDialog loads
               default_subject/default_message from Mailer Settings. */
            customerEmail={customerEmail}
          />
        </div>
      )}
    </div>
  );
};

export default ModalDialogFinancing;